// - - - - - - - - - -
// complianceHoldCell.js — #453
//
// isComplianceHoldCell(scheduleArray, payrollStr, dateStr) → boolean
//
// WHY THIS EXISTS (SUP-9743): compliance-hold and inactive markers are
// SYNTHETIC attributes built in `schedule.context.js` — in-memory only, with
// no MongoDB Attributes document behind them. They used to carry
// `locked: true` purely to borrow the #174 day-lock's colorization, which made
// every held cell claim a user lock nobody could clear: the right-click menu
// offered "Unlock this day", and the unlock handler — which looks for a REAL
// Mongo doc to flip — found none and returned silently, forever. It also froze
// the day's jobs (`draggable={!isLockedCell …}`), so work could not be moved
// OFF an installer who had just gone on hold.
//
// The markers now carry only their honest discriminator (`complianceHold`),
// and this predicate reads it. Callers combine it with the real lock for the
// INFORMATIONAL treatments (border/tint, 🔍 suppression) while leaving the
// OWNERSHIP treatments (🔒 icon, Lock/Unlock menu, drag-out gate) keyed to a
// genuine lock only.
//
// Extracted + tested per CLAUDE.md's "when to extract a tiny utility module":
// it drives a user-facing decision, is used in a production render path, and
// mirrors `isLocked`'s cell-matching so the two cannot drift.

/**
 * Find the projected ATTRIBUTE entry for one installer-day cell.
 * Mirrors the matching used by `isLocked` in optimizerScreen.js: same
 * installer key, same date key, attributes only (never job tiles).
 *
 * @param {Array} scheduleArray - projected schedule entries for the grid
 * @param {string} payrollStr - formatted payroll ("" for the unassigned row)
 * @param {string} dateStr - the cell's date, as used in `entry.start`
 * @returns {Object|undefined} the first matching attribute entry
 */
export function findCellAttribute(scheduleArray, payrollStr, dateStr) {
  if (!Array.isArray(scheduleArray)) return undefined;
  return scheduleArray.find(
    (entry) =>
      entry &&
      entry.attribute &&
      entry.installer === payrollStr &&
      entry.start === dateStr
  );
}

/**
 * True when the cell carries a compliance-hold / inactive marker (#352, #378).
 * Never infers from `locked` — a real user lock is a separate concept (#453).
 */
export function isComplianceHoldCell(scheduleArray, payrollStr, dateStr) {
  return Boolean(
    findCellAttribute(scheduleArray, payrollStr, dateStr)?.complianceHold
  );
}
